import { AgentState, AgentStateEvent } from '@nice-ccf/common-sdk';
import { AgentSessionManager, ApiUriConstants, HttpClient, HttpRequestInit, HttpResponse, HttpUtilService, Logger } from '@nice-ccf/core-sdk';
import { AgentStates } from '../enum/agent-states';
import { Subject } from 'rxjs/internal/Subject';

/**
 * Class to perform Agent state's
 */
export class AgentStateService {
  private static singleton: AgentStateService;
  protected logger: Logger = new Logger('SDK', 'AgentStateService');
  private agentSession = <AgentSessionManager>{};
  private agentStateDetails!: AgentStateEvent;

  public agentStateSubject: Subject<AgentStateEvent> = new Subject();
  protected utilService: HttpUtilService = new HttpUtilService();

  /**
   * Method to create singleton object of the class
   * ```
   * @example
   * const agentStateManaget = AgentStateManager.instance;
   * ```
   */
  public static get instance(): AgentStateService {
    if (!AgentStateService.singleton) {
      AgentStateService.singleton = new AgentStateService();
    }
    return AgentStateService.singleton;
  }

  /**
   *
   * ```
   * @example
   * const agentState = new AgentStateService();
   * ```
   */
  constructor() {
    this.agentSession = AgentSessionManager.instance;
    this.agentSession.agentStateSubject.subscribe(
      (agentState: AgentStateEvent) => {
        this.logger.debug('agentStateSubject ', JSON.stringify(agentState));
        this.agentStateDetails = agentState;
        this.updateAgentStates();
      }
    );
  }

  /**
   * Service method to set agent state
   * @param agentState - Updated agent state along with reason
   * @returns - response from the setAgentState api
   * @example
   * ```
   * const agentState = { state: 'Unavailable', reason:'Break' };
   * setAgentState(agentState)
   * ```
   */
  setAgentState(agentState: AgentState): Promise<HttpResponse> {
    const setAgentStateUri = ApiUriConstants.SET_AGENT_STATE_URI;
    const baseUri = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const url =
      baseUri +
      setAgentStateUri.replace('{sessionId}', this.agentSession.getSessionId());
    const reqInit: HttpRequestInit = {
      headers: this.utilService.initHeader(
        this.agentSession.auth.getAuthToken().accessToken,
        ''
      ).headers,
      body: {
        state: agentState.state,
        reason: agentState.reason,
      },
    };
    return HttpClient.post(url, reqInit);
  }

  /**
   * Method to update agent states
   * ```
   * @example
   * this.updateAgentStates();
   * ```
   */
  private updateAgentStates() {
    this.updateAgentCurrentState();
    this.agentStateSubject.next(this.agentStateDetails);
  }

  /**
   * Method to update agent current state
   * ```
   * @example
   * this.updateAgentCurrentState();
   * ```
   */
  private updateAgentCurrentState() {
    const currentState =
      this.agentStateDetails.currentState.state.toLowerCase();
    if (this.isAgentInWorkingState(currentState)) {
      this.agentStateDetails.currentState.state = 'Working';
    } else {
      this.agentStateDetails.currentState.state = currentState;
    }
  }

  /**
   * Method to check the agent is working or not
   * @param state - Agent state
   * ```
   * @example
   * this.isAgentInWorkingState();
   * ```
   */
  private isAgentInWorkingState(state: string) {
    const currentState = state.toLowerCase();
    return (
      currentState === AgentStates.OutboundContact.toLowerCase() ||
      currentState === AgentStates.InboundContact.toLowerCase() ||
      currentState === AgentStates.InboundConsult.toLowerCase() ||
      currentState === AgentStates.OutboundConsult.toLowerCase()
    );
  }
}
